import type { Interview, InterviewFeedback } from 'store/reducers/jobReducer';
import type { Uuid } from 'store/reducers/types';
import { STEPS_LABELS, stepsStatuses } from './constants';

export const INTERVIEW_KINDS = {
  HR: 'HR',
  TECHNICAL: 'Technical',
  MANAGEMENT: 'Management',
} as const;

export type InterviewKind = (typeof INTERVIEW_KINDS)[keyof typeof INTERVIEW_KINDS];

interface FeedbackHistoryStep {
  uuid: Uuid;
  name?: string;
  ordering_number?: number;
}

export const getInterviewKind = (stepName?: string): InterviewKind | undefined => {
  if (stepName === STEPS_LABELS.CONTACT_WITH_CANDIDATE || stepName === STEPS_LABELS.CV_SCREENING) {
    return INTERVIEW_KINDS.HR;
  }

  if (stepName === STEPS_LABELS.TECHNICAL_INTERVIEW) return INTERVIEW_KINDS.TECHNICAL;
  if (stepName === STEPS_LABELS.MANAGEMENT_INTERVIEW) return INTERVIEW_KINDS.MANAGEMENT;

  return undefined;
};

export const supportsAdditionalInterviews = (interviewKind?: InterviewKind): boolean =>
  interviewKind === INTERVIEW_KINDS.TECHNICAL || interviewKind === INTERVIEW_KINDS.MANAGEMENT;

export const isAdditionalInterviewStatus = (stepStatus?: string): boolean =>
  stepStatus === stepsStatuses.provideFeedback || stepStatus === stepsStatuses.waitingForFinalDecision;

export const canShowAddInterview = (interviewKind?: InterviewKind, rejected?: boolean, stepStatus?: string): boolean =>
  !rejected && supportsAdditionalInterviews(interviewKind) && isAdditionalInterviewStatus(stepStatus);

export const getInterviewSequenceNumber = (_interview: Interview, index: number): number => index + 1;

export const getCurrentInterview = (interviews: Interview[]): Interview | undefined =>
  interviews.find(interview => interview.is_current) || interviews[interviews.length - 1];

const getEarliestInterview = (interviews: Interview[]): Interview | undefined => interviews[0];

export const getFeedbackInterviewId = (feedback?: { interview?: InterviewFeedback['interview'] }): Uuid | undefined => {
  if (!feedback?.interview) return undefined;
  if (typeof feedback.interview === 'string') return feedback.interview;
  return feedback.interview.uuid;
};

export const getFeedbackInterviewSequence = (feedback?: {
  interview?: InterviewFeedback['interview'];
}): number | undefined => {
  if (!feedback?.interview || typeof feedback.interview === 'string') return undefined;
  const sequence = feedback.interview.sequence_number;
  return sequence != null && sequence > 0 ? sequence : undefined;
};

export const normalizeInterviewFeedback = (feedback: InterviewFeedback): InterviewFeedback => {
  const interviewId = getFeedbackInterviewId(feedback);
  if (!interviewId) return feedback;

  return {
    ...feedback,
    interview:
      typeof feedback.interview === 'object' && feedback.interview
        ? { ...feedback.interview, uuid: feedback.interview.uuid || interviewId }
        : interviewId,
  };
};

export const withFeedbackInterview = (body: object, interviewId?: Uuid): object =>
  interviewId ? { ...body, interview: interviewId } : body;

export const isFeedbackPosted = (feedback: InterviewFeedback): boolean =>
  !!feedback.posted_at || (!!feedback.comment && !feedback.is_draft);

export const feedbackBelongsToInterview = (
  feedback: InterviewFeedback,
  interviewId?: Uuid,
  fallbackInterviewId?: Uuid
): boolean => {
  const linkedInterviewId = getFeedbackInterviewId(feedback);
  if (linkedInterviewId) return linkedInterviewId === interviewId;
  return !!interviewId && interviewId === fallbackInterviewId;
};

export interface InterviewFeedbackGroup {
  interviewId?: Uuid;
  sequence: number;
  isCurrent: boolean;
  feedbacks: InterviewFeedback[];
  feedbacksPosted?: number;
  feedbacksTotal?: number;
}

export const groupFeedbacksByInterview = (
  feedbacks: InterviewFeedback[],
  interviews: Interview[]
): InterviewFeedbackGroup[] => {
  const currentInterviewId = getCurrentInterview(interviews)?.uuid;
  const groups = new Map<string, InterviewFeedbackGroup>();
  const interviewKeys = new Set<string>();

  interviews.forEach((interview, index) => {
    if (!interview.uuid) return;
    const key = String(interview.uuid);
    interviewKeys.add(key);
    groups.set(key, {
      interviewId: interview.uuid,
      sequence: getInterviewSequenceNumber(interview, index),
      isCurrent: !!interview.is_current || interview.uuid === currentInterviewId,
      feedbacks: [],
      feedbacksPosted: interview.feedbacks_posted,
      feedbacksTotal: interview.feedbacks_total,
    });
  });

  feedbacks.forEach(feedback => {
    const linkedInterviewId = getFeedbackInterviewId(feedback);
    const fallbackInterviewId = isFeedbackPosted(feedback)
      ? getEarliestInterview(interviews)?.uuid
      : currentInterviewId;
    const interviewId = linkedInterviewId || fallbackInterviewId;
    const key = String(interviewId || 'unassigned');
    const sequenceFromFeedback = getFeedbackInterviewSequence(feedback);

    if (!groups.has(key)) {
      groups.set(key, {
        interviewId,
        sequence: sequenceFromFeedback ?? 0,
        isCurrent: interviewId === currentInterviewId,
        feedbacks: [],
      });
    } else if (!interviewKeys.has(key) && sequenceFromFeedback != null) {
      groups.get(key)!.sequence = sequenceFromFeedback;
    }

    groups.get(key)!.feedbacks.push(feedback);
  });

  return Array.from(groups.values()).sort((first, second) => first.sequence - second.sequence);
};

export const getFeedbackHistorySteps = <T extends FeedbackHistoryStep>(steps: T[], currentStep?: T): T[] => {
  if (!currentStep) return [];

  const currentStepIndex = steps.findIndex(step => step.uuid === currentStep.uuid);
  const currentStepOrder = currentStep.ordering_number ?? currentStepIndex;

  return steps
    .map((step, index) => ({ step, order: step.ordering_number ?? index }))
    .filter(({ step, order }) => !!getInterviewKind(step.name) && order <= currentStepOrder)
    .sort((first, second) => {
      if (first.step.uuid === currentStep.uuid) return -1;
      if (second.step.uuid === currentStep.uuid) return 1;

      return second.order - first.order;
    })
    .map(({ step }) => step);
};
